import { ExternalLink } from "lucide-react";
import { useData } from "@/context/DataContext";
import { resolveUrl } from "@/lib/api";
import { Reveal, SectionHeader } from "@/components/shared/Reveal";

const TIERS = [
  { key: "Huvudpartner", label: "Huvudpartner" },
  { key: "Officiell Partner", label: "Officiella partners" },
  { key: "Partner", label: "Partners" },
];

const PartnerCard = ({ s }) => {
  const content = (
    <>
      <div className="flex items-center justify-center w-full h-28 shrink-0">
        {s.logo_url ? (
          <img
            src={resolveUrl(s.logo_url)}
            alt={s.name}
            className="h-full w-[82%] object-contain transition-transform duration-300 group-hover:scale-[1.04]"
          />
        ) : (
          <span className="font-display font-black uppercase text-white text-2xl line-clamp-2">{s.name}</span>
        )}
      </div>
      <p title={s.description || ""} className="mt-5 h-[4.5rem] w-full text-sm text-white/60 leading-6 line-clamp-3 overflow-hidden text-ellipsis">{s.description}</p>
      {s.url && (
        <span className="mt-auto pt-4 inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-widest text-amber-500 group-hover:gap-2.5 transition-all">
          Besök partner <ExternalLink className="w-3.5 h-3.5" />
        </span>
      )}
    </>
  );

  const cls = "group relative flex flex-col items-center text-center h-[300px] w-full p-7 rounded-2xl border border-white/10 bg-[#14161A] hover:border-amber-500/50 hover:bg-[#181b20] transition-all duration-300";

  return s.url ? (
    <a href={s.url} target="_blank" rel="noreferrer" data-testid={`sponsor-${s.id}`} className={cls}>
      {content}
    </a>
  ) : (
    <div data-testid={`sponsor-${s.id}`} className={cls}>{content}</div>
  );
};

export const PartnersSection = ({ hideHeader }) => {
  const { data } = useData();
  const sponsors = data.sponsors || [];
  if (!sponsors.length) return null;

  const byTier = (tier) => sponsors.filter((s) => (s.tier || "Partner") === tier);

  return (
    <section data-testid="partners-section" className={`relative ${hideHeader ? "pt-8 pb-24 sm:pb-32" : "py-24 sm:py-32"}`}>
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        {!hideHeader && (
          <Reveal>
            <SectionHeader kicker="Partners" title="De som gör det möjligt" subtitle="Novemberkåsan 2026 körs tack vare våra partners." center />
          </Reveal>
        )}

        {TIERS.map(({ key, label }) => {
          const list = byTier(key);
          if (!list.length) return null;
          return (
            <div key={key} className="mt-16 first:mt-0">
              <Reveal>
                <div className="mb-10 text-center">
                  <h3 className="font-display text-3xl sm:text-4xl font-black uppercase tracking-tight text-white leading-none">{label}</h3>
                  <div className="mx-auto mt-4 h-1 w-16 rounded-full bg-amber-500" />
                </div>
              </Reveal>
              <div data-testid="partners-grid" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {list.map((s, i) => (
                  <Reveal key={s.id} delay={i * 0.06} className="h-full">
                    <PartnerCard s={s} />
                  </Reveal>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};
